// Signage → Impacto en ventas (/signage/uplift). LIFT OBSERVADO: para cada
// producto exhibido en pantalla, compara sus ventas en local-días EXPUESTOS vs
// CONTROL (sin exposición). Aprovecha que proof-of-play y POS están en la misma
// BD. Es correlación (con control geográfico/temporal), no causalidad.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { fieldCls, money } from '@/features/pos/shared';
import { sigRpc } from './shared';

interface Row {
  codigo: string | null; nombre: string; u_exp: number; u_ctrl: number; monto_exp: number;
  ld_exp: number; ld_ctrl: number; avg_exp: number | null; avg_ctrl: number | null; uplift_pct: number | null;
}
interface Data { desde: string; hasta: string; productos: Row[] }

const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export function Uplift() {
  const [desde, setDesde] = useState(haceDias(29));
  const [hasta, setHasta] = useState(hoy());
  const { data, isFetching } = useQuery<Data>({
    queryKey: ['sig-uplift', desde, hasta],
    queryFn: () => sigRpc<Data>('sig_uplift', { _desde: desde, _hasta: hasta }),
    staleTime: 0,
  });
  const rows = useMemo(() => data?.productos ?? [], [data]);
  const conMedicion = useMemo(() => rows.filter((r) => r.uplift_pct != null), [rows]);
  const upliftProm = conMedicion.length
    ? conMedicion.reduce((a, r) => a + (r.uplift_pct ?? 0), 0) / conMedicion.length : null;

  const upColor = (v: number | null) => v == null ? 'text-muted-foreground' : v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : '';

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Impacto en ventas</h1>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <input type="date" className={`${fieldCls} !w-auto`} value={desde} onChange={(e) => setDesde(e.target.value)} />
          <span className="text-muted-foreground">a</span>
          <input type="date" className={`${fieldCls} !w-auto`} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
      </div>

      {upliftProm != null && (
        <Card><CardContent className="p-4 flex items-center gap-4">
          <div className={`text-3xl font-bold ${upColor(upliftProm)}`}>{upliftProm > 0 ? '+' : ''}{upliftProm.toFixed(1)}%</div>
          <div className="text-sm text-muted-foreground">lift observado promedio · {conMedicion.length} producto(s) con medición (expuesto vs. control)</div>
        </CardContent></Card>
      )}

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th>
            <th className="py-2 px-3 text-left">Producto</th>
            <th className="py-2 px-3 text-right">u/local-día expuesto</th>
            <th className="py-2 px-3 text-right">u/local-día control</th>
            <th className="py-2 px-3 text-right">Lift</th>
            <th className="py-2 px-3 text-right">Ventas expuesto</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{r.nombre} {r.codigo && <span className="text-xs text-muted-foreground">{r.codigo}</span>}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.avg_exp ?? '—'} <span className="text-xs text-muted-foreground">({r.u_exp}u/{r.ld_exp}d)</span></td>
                <td className="py-2 px-3 text-right tabular-nums">{r.avg_ctrl ?? '—'} <span className="text-xs text-muted-foreground">{r.ld_ctrl ? `(${r.u_ctrl}u/${r.ld_ctrl}d)` : ''}</span></td>
                <td className={`py-2 px-3 text-right font-semibold tabular-nums ${upColor(r.uplift_pct)}`}>
                  {r.uplift_pct == null ? 's/ control' : `${r.uplift_pct > 0 ? '+' : ''}${r.uplift_pct}%`}
                </td>
                <td className="py-2 px-3 text-right font-mono">{money(r.monto_exp)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">
                {isFetching ? 'Calculando…' : 'Sin datos aún: aparece cuando las pantallas exhiban productos (proof-of-play) y haya ventas de esos SKU en el POS.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        <strong>Lift observado</strong>: unidades por local-día del SKU cuando estuvo en pantalla vs. cuando no (control geográfico/temporal
        dentro de la empresa). Es <strong>correlación</strong>, no causalidad garantizada; influyen precio, stock y ubicación. Los productos
        exhibidos en todos los locales/días no tienen control comparable (aparecen “s/ control”).
      </p>
    </div>
  );
}
